import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Container,
    Grid,
    Card,
    Chip,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Avatar,
    TextField,
    InputAdornment,
    IconButton,
    Divider,
    Button
} from '@mui/material';
import { motion } from 'framer-motion';
import {
    Chat as ChatIcon,
    Store as StoreIcon, // Description yerine
    AccessTime as AccessTimeIcon,
    Inventory as InventoryIcon, // Assignment yerine
    TrendingUp as TrendingUpIcon, // Support yerine
    CheckCircle as CheckCircleIcon,
    People as PeopleIcon, // Psychology yerine
    Speed as SpeedIcon,
    Assessment as AssessmentIcon, // Groups yerine
    SmartToy as SmartToyIcon,
    Send as SendIcon,
    AttachMoney as AttachMoneyIcon, // Phone yerine
    Campaign as CampaignIcon,
    ThumbUpOffAlt as ThumbUpOffAltIcon,
    ThumbDownOffAlt as ThumbDownOffAltIcon,
    ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { AccountCircle as AccountCircleIcon } from '@mui/icons-material';

const FranchiseManagement = () => {
    const [chatMessage, setChatMessage] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [chatHistory, setChatHistory] = useState(() => [
        {
            type: 'bot',
            message: 'Merhaba! Franchise Destek Asistanı’na hoş geldiniz. Bayi performansı, ciro, stok veya operasyonel süreçler hakkında size nasıl yardımcı olabilirim?',
            time: '09:00'
        },
        {
            type: 'user',
            message: 'İstanbul bölgesindeki bayilerin geçen aya göre ciro karşılaştırmasını yapabilir misin?',
            time: '09:01'
        },
        {
            type: 'bot',
            message: 'İstanbul bölgesindeki bayilerimizin geçen aya göre toplam cirosu %8.5 oranında artış gösterdi. Beşiktaş ve Kadıköy şubeleri hedeflerinin %12 üzerinde seyrederken, Bakırköy şubesinde stok devir hızının artırılması öneriliyor. Detaylı analiz tablosunu hazırladım.',
            time: '09:01'
        }
    ]);
    const chatBoxRef = useRef(null);
    const isDemoLocked = true; // Giriş kutusunu kilitleyerek demo olduğunu netleştir

    // Sayfa yüklendiğinde en üste scroll
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Demo chat mesajları
    // Hızlı sorular (tıklanabilir)
    const quickQuestions = [
        'Günlük ciro ne kadar?',
        'Kritik stok var mı?',
        'Personel vardiyası?',
        'En çok satan ürünler?',
        'Mağaza puanım kaç?'
    ];

    // Pazarlama vurgusu (tüm yanıtlara eklenecek kısa CTA)
    const marketingSuffix = `—\n**Mağaza Asistanı ile:** veriye anında erişim, daha hızlı kararlar, artan ciro.\nDetaylı bilgi için: **info@dcat.com.tr** | (0212) 391 01 80`;
    const enhance = (s) => `${s}\n\n${marketingSuffix}`;

    // Gelişmiş kural tabanlı Franchise chatbot yanıt motoru
    const getBotResponse = (text) => {
        const t = text.toLowerCase();

        // 1️⃣ Ciro & Satış Performansı
        if (
            t.includes("ciro") ||
            t.includes("satış") ||
            t.includes("kazanç") ||
            t.includes("gelir")
        ) {
            return enhance(`**Günlük Satış Performansı**
        - **Bugünkü Ciro:** 42.500 TL (%12 artış)
        - **İşlem Sayısı:** 124 Fiş
        - **Sepet Ortalaması:** 342 TL
        
        Bölgesel kıyaslama:
        - Mağazanız, İstanbul bölge ortalamasının **%5 üzerinde** performans gösteriyor.
        - En çok satış yapılan saat aralığı: **12:00 - 14:00**`);
        }

        // 2️⃣ Stok & Envanter
        if (t.includes("stok") || t.includes("ürün") || t.includes("envanter") || t.includes("kritik")) {
            return enhance(`**Stok Durumu Raporu**
    1. **Kritik Seviye (Acil Sipariş):**
       - Yazlık Gömlek (Mavi/L) - 2 Adet kaldı
       - Kot Pantolon (Siyah/32) - 1 Adet kaldı
    
    2. **Transfer Önerisi:**
       - Kadıköy şubesinde fazla stok bulunan "Basic T-Shirt" ürününden 20 adet talep edebilirsiniz.
    
    İpucu: Otomatik sipariş oluşturmamı ister misiniz?`);
        }

        // 3️⃣ Personel & Vardiya
        if (t.includes("personel") || t.includes("vardiya") || t.includes("çalışan") || t.includes("izin")) {
            return enhance(`**Personel ve Vardiya Durumu**
    - **Aktif Çalışan Sayısı:** 4 (Şu an mağazada)
    - **Vardiya Lideri:** Ahmet Yılmaz
    - **İzinli:** Ayşe Demir (Yıllık İzin - Dönüş: Pazartesi)
    
    Uyarı: Yarın sabah vardiyasında 1 kişi eksik görünüyor, takviye planlamanız gerekebilir.`);
        }

        // 4️⃣ Kampanya & Promosyon
        if (t.includes("kampanya") || t.includes("indirim") || t.includes("promosyon")) {
            return enhance(`**Aktif Kampanyalar**
    - **3 Al 2 Öde:** Temel ürün grubunda aktif (Bitiş: Pazar)
    - **%20 İndirim:** Yeni sezon ürünlerinde (Sadece bugün)
    
    Performans Etkisi:
    - Kampanyalı ürünler bugünkü cironun **%35'ini** oluşturuyor.
    - Müşteri dönüşüm oranı **%4 arttı**.`);
        }

        // 5️⃣ Mağaza Puanı & Denetim
        if (t.includes("puan") || t.includes("denetim") || t.includes("kalite") || t.includes("hedef")) {
            return enhance(`**Mağaza Performans Karnesi**
    - **Genel Puan:** 4.2 / 5.0
    - **Müşteri Memnuniyeti:** %92
    - **Görsel Düzen:** %95 (Son denetimden)
    - **Eksik Alanlar:** Kasa önü ürün yerleşimi (İyileştirme gerekiyor)
    
    Hedef: Bu ay %5 büyüme hedefinin **%2 önündesiniz**.`);
        }

        // Genel cevap
        return enhance(`**Sorunuzu not aldım.**
    Operasyonel verilerinize (Ciro, Stok, Personel vb.) POS ve ERP entegrasyonu üzerinden anlık erişebilirsiniz.`);
    };

    const scheduleBotReply = (text) => {
        const reply = getBotResponse(text);
        const delay = Math.min(2000, Math.max(800, reply.length * 30));
        setIsTyping(true);
        setTimeout(() => {
            const time = new Date().toLocaleTimeString().slice(0, 5);
            setChatHistory((prev) => ([...prev, { type: 'bot', message: reply, time }]));
            setIsTyping(false);
        }, delay);
    };

    const handleQuickQuestion = (msg) => {
        const time = new Date().toLocaleTimeString().slice(0, 5);
        setChatHistory((prev) => ([...prev, { type: 'user', message: msg, time }]));
        scheduleBotReply(msg);
    };

    const handleSendMessage = () => {
        if (chatMessage.trim()) {
            const now = new Date().toLocaleTimeString().slice(0, 5);
            const msg = chatMessage;
            setChatHistory((prev) => ([...prev, { type: 'user', message: msg, time: now }]));
            setChatMessage('');
            scheduleBotReply(msg);
        }
    };

    const handleFeedback = (index, value) => {
        setChatHistory((prev) => {
            const next = [...prev];
            const current = next[index];
            if (!current || current.type !== 'bot') return prev;
            const feedback = current.feedback === value ? undefined : value;
            next[index] = { ...current, feedback };
            return next;
        });
    };

    // Mesaj eklendikçe sohbet kutusunu alta kaydır
    useEffect(() => {
        if (chatBoxRef.current) {
            chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
        }
    }, [chatHistory, isTyping]);

    // Temel özellikler
    const features = [
        {
            icon: <TrendingUpIcon />,
            title: 'Performans İzleme',
            description: 'Bayi performansı, ciro, stok ve hedef verilerini tek ekranda anlık olarak izleme.'
        },
        {
            icon: <StoreIcon />,
            title: 'Doküman Analizi',
            description: 'Sözleşme, prosedür ve eğitim dokümanlarından bilgi çıkararak doğru yanıtlar üretme.'
        },
        {
            icon: <SpeedIcon />,
            title: 'Sorun Analizi & Yönlendirme',
            description: 'Bayi sorunlarını analiz ederek ilgili birim ya da kişiye doğru yönlendirme sağlama.'
        },
        {
            icon: <CheckCircleIcon />,
            title: 'Süreç Otomasyonu',
            description: 'Bayi açılış, kapanış, denetim ve operasyon süreçlerini adım adım otomatikleştirme.'
        },
        {
            icon: <AssessmentIcon />,
            title: 'Gelişmiş Filtreleme',
            description: 'Bölge, bayi tipi, marka, kategori ve dönem bazlı detaylı raporlama ve filtreleme.'
        },
        {
            icon: <PeopleIcon />,
            title: 'Güvenli Veri Yönetimi',
            description: 'Rol bazlı yetkilendirme ve güvenli veri yönetimi ile hassas bilgileri koruma.'
        }
    ];

    // Kullanım senaryoları
    const useCases = [
        {
            title: 'Satış & Ciro Takibi',
            icon: <AttachMoneyIcon />,
            items: [
                'Anlık ciro sorgulama',
                'Saatlik satış performansı',
                'Sepet ortalaması analizi',
                'Hedef gerçekleşme oranı'
            ]
        },
        {
            title: 'Stok & Tedarik',
            icon: <InventoryIcon />,
            items: [
                'Kritik stok uyarıları',
                'Çok satan ürün takibi',
                'Mağazalar arası transfer',
                'Otomatik sipariş önerisi'
            ]
        },
        {
            title: 'Mağaza Operasyonu',
            icon: <StoreIcon />,
            items: [
                'Vardiya planlaması',
                'Mağaza açılış/kapanış onayı',
                'Görsel düzen denetimi',
                'Enerji tüketim takibi'
            ]
        },
        {
            title: 'Performans Yönetimi',
            icon: <TrendingUpIcon />,
            items: [
                'Bölge/Şube sıralaması',
                'Personel satış hedefleri',
                'Gizli müşteri skorları',
                'Kayıp/Kaçak oranları'
            ]
        }
    ];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa' }}>
            {/* Hero Section */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #FF6B6B 0%, #d32f2f 100%)', // Franchise/Retail için daha canlı/kırmızımsı bir ton
                    color: 'white',
                    py: { xs: 8, md: 12 },
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <motion.div
                                initial={{ opacity: 0, x: -50 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                            >
                                <Chip
                                    label="Yapay Zeka Destekli Perakende Çözümü"
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        color: 'white',
                                        mb: 2,
                                        fontWeight: 600
                                    }}
                                />
                                <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700, fontSize: { xs: '2rem', md: '2.5rem' } }}>
                                    Franchise Destek Asistanı
                                </Typography>
                                <Typography variant="h5" sx={{ mb: 3, opacity: 0.95, lineHeight: 1.6 }}>
                                    Bayilik ağı bulunan şirketin, bayilerinin her türlü operasyonel sorularını cevaplayan, anlık verilerle görünür hale getirilmesini sağlayan yapay zeka bayi dijital asistanı
                                </Typography>

                            </motion.div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                            >
                                {/* Demo Chat Interface */}
                                <Paper sx={{ p: 3, borderRadius: 3, maxWidth: 400, mx: 'auto', position: 'relative' }}>
                                    <Chip label="Canlı Demo" size="small" sx={{ position: 'absolute', top: 8, right: 8, bgcolor: '#e0e0e0', color: '#424242', fontWeight: 600 }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <SmartToyIcon sx={{ color: '#d32f2f', mr: 1 }} />
                                        <Typography variant="h6" sx={{ color: '#d32f2f', fontWeight: 600 }}>
                                            Franchise Asistanı
                                        </Typography>
                                    </Box>
                                    {/* Mesajlar alanı */}
                                    <Box sx={{ height: 450, overflowY: 'auto', mb: 2 }} ref={chatBoxRef}>
                                        {chatHistory.map((msg, index) => (
                                            <Box key={index} sx={{ mb: 1 }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start',
                                                        alignItems: 'flex-end',
                                                        gap: 1
                                                    }}
                                                >
                                                    {msg.type !== 'user' && (
                                                        <Avatar sx={{ bgcolor: '#d32f2f', width: 28, height: 28 }}>
                                                            <SmartToyIcon fontSize="small" />
                                                        </Avatar>
                                                    )}
                                                    <Paper
                                                        sx={{
                                                            p: 1.5,
                                                            display: 'inline-block',
                                                            maxWidth: '80%',
                                                            bgcolor: msg.type === 'user' ? '#d32f2f' : '#f5f5f5',
                                                            color: msg.type === 'user' ? 'white' : 'black',
                                                            borderRadius: msg.type === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                                                            overflowWrap: 'anywhere',
                                                            wordBreak: 'break-word'
                                                        }}
                                                    >
                                                        <Typography variant="body2" sx={{
                                                            whiteSpace: 'pre-wrap',
                                                            lineHeight: 1.6,
                                                            overflowWrap: 'anywhere',
                                                            wordBreak: 'break-word'
                                                        }}>
                                                            {msg.message}
                                                        </Typography>
                                                    </Paper>
                                                    {msg.type === 'user' && (
                                                        <Avatar sx={{ bgcolor: '#424242', width: 28, height: 28 }}>
                                                            <AccountCircleIcon fontSize="small" />
                                                        </Avatar>
                                                    )}
                                                </Box>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        mt: 0.5,
                                                        gap: 0.5,
                                                        justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start'
                                                    }}
                                                >
                                                    <Typography variant="caption" sx={{ opacity: 0.7 }}>{msg.time}</Typography>
                                                    {msg.type === 'bot' && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                                                            <IconButton size="small" onClick={() => handleFeedback(index, 'like')}>
                                                                <ThumbUpOffAltIcon fontSize="inherit" color={msg.feedback === 'like' ? 'primary' : 'inherit'} />
                                                            </IconButton>
                                                            <IconButton size="small" onClick={() => handleFeedback(index, 'dislike')}>
                                                                <ThumbDownOffAltIcon fontSize="inherit" color={msg.feedback === 'dislike' ? 'error' : 'inherit'} />
                                                            </IconButton>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                        ))}
                                        {isTyping && (
                                            <Box sx={{ mb: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                                                    <Avatar sx={{ bgcolor: '#d32f2f', width: 28, height: 28 }}>
                                                        <SmartToyIcon fontSize="small" />
                                                    </Avatar>
                                                    <Paper sx={{ p: 1.5, display: 'inline-block', bgcolor: '#f5f5f5', color: 'black', borderRadius: '16px 16px 16px 4px' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                                            <Box component={motion.div} animate={{ y: [0, -3, 0], opacity: [0.4, 1, 0.4] }} transition={{ duration: 0.9, repeat: Infinity }} sx={{ width: 6, height: 6, bgcolor: '#9e9e9e', borderRadius: '50%' }} />
                                                            <Box component={motion.div} animate={{ y: [0, -3, 0], opacity: [0.4, 1, 0.4] }} transition={{ duration: 0.9, repeat: Infinity, delay: 0.2 }} sx={{ width: 6, height: 6, bgcolor: '#9e9e9e', borderRadius: '50%' }} />
                                                            <Box component={motion.div} animate={{ y: [0, -3, 0], opacity: [0.4, 1, 0.4] }} transition={{ duration: 0.9, repeat: Infinity, delay: 0.4 }} sx={{ width: 6, height: 6, bgcolor: '#9e9e9e', borderRadius: '50%' }} />
                                                        </Box>
                                                    </Paper>
                                                </Box>
                                                <Typography variant="caption" sx={{ opacity: 0.7, mt: 0.5 }}>Analiz ediliyor…</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                    {/* Hızlı sorular */}
                                    <Box sx={{
                                        display: 'flex',
                                        gap: 1,
                                        mb: 1.25,
                                        overflowX: 'auto',
                                        overflowY: 'hidden',
                                        whiteSpace: 'nowrap',
                                        px: 0.5,
                                        '&::-webkit-scrollbar': { height: 6 },
                                        '&::-webkit-scrollbar-thumb': { backgroundColor: '#b5b5b5', borderRadius: 8 }
                                    }}>
                                        {quickQuestions.map((msg, index) => (
                                            <Chip
                                                key={index}
                                                label={msg}
                                                size="small"
                                                clickable
                                                onClick={() => handleQuickQuestion(msg)}
                                                sx={{
                                                    fontSize: '0.9rem',
                                                    mr: 0.5
                                                }}
                                            />
                                        ))}
                                    </Box>
                                    <TextField
                                        value={chatMessage}
                                        onChange={(e) => setChatMessage(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleSendMessage(); }}
                                        placeholder={isDemoLocked ? 'Demo Modu - butonları kullanınız' : 'Sorunuzu yazın...'}
                                        size="small"
                                        fullWidth
                                        disabled={isDemoLocked}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={handleSendMessage} aria-label="mesaj gönder" disabled={isDemoLocked}>
                                                        <SendIcon />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                        * Demo verileriyle çalışmaktadır.
                                    </Typography>

                                </Paper>
                            </motion.div>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Özellikler */}
            <Box sx={{ bgcolor: 'white', py: 8 }}>
                <Container maxWidth="lg">
                    <Box textAlign="center" mb={6}>
                        <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                            Asistan Özellikleri
                        </Typography>
                        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
                            Bayi operasyonlarınızı optimize eden güçlü yapay zeka yetenekleri
                        </Typography>
                    </Box>
                    <Grid container spacing={4}>
                        {features.map((feature, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <motion.div
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                >
                                    <Card sx={{ p: 3, height: '100%', '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' }, transition: 'all 0.3s' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <Avatar sx={{ bgcolor: '#d32f2f', mr: 2, width: 56, height: 56 }}>
                                                {feature.icon}
                                            </Avatar>
                                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                {feature.title}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                            {feature.description}
                                        </Typography>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Kullanım Senaryoları */}
            <Box sx={{ bgcolor: '#f5f5f5', py: 8 }}>
                <Container maxWidth="lg">
                    <Box textAlign="center" mb={6}>
                        <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                            Asistanın Yanıtlayabildiği Konular
                        </Typography>
                        <Typography variant="h6" color="text.secondary">
                            Bayi operasyonu ve performansına dair merak edilen tüm sorulara anlık cevaplar
                        </Typography>
                    </Box>
                    <Grid container spacing={4}>
                        {useCases.map((useCase, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <motion.div
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                >
                                    <Card sx={{ p: 3, height: '100%', bgcolor: 'white', '&:hover': { boxShadow: 6 }, transition: 'all 0.3s' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <Avatar sx={{ bgcolor: '#d32f2f', mr: 1.5 }}>
                                                {useCase.icon}
                                            </Avatar>
                                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#c62828', fontSize: '1.1rem' }}>
                                                {useCase.title}
                                            </Typography>
                                        </Box>
                                        <List dense>
                                            {useCase.items.map((item, itemIndex) => (
                                                <ListItem key={itemIndex} sx={{ px: 0, py: 0.5 }}>
                                                    <ListItemIcon sx={{ minWidth: 28 }}>
                                                        <CheckCircleIcon color="error" fontSize="small" />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={item}
                                                        primaryTypographyProps={{ variant: 'body2' }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Ücretsiz Demo Talep Et CTA */}
            <Box
                component={motion.div}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                sx={{
                    py: { xs: 8, md: 12 },
                    background: 'linear-gradient(180deg, #0b1426 0%, #0a1020 100%)',
                    textAlign: 'center'
                }}
            >
                <Container maxWidth="md">
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 800,
                            color: '#ffffff',
                            mb: 3,
                            fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem' },
                            textShadow: '2px 0 0 rgba(255,60,80,0.55), -2px 0 0 rgba(60,170,255,0.55)',
                            letterSpacing: '-0.5px'
                        }}
                    >
                        Ücretsiz Demo Talep Et
                    </Typography>
                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.72)',
                            maxWidth: 720,
                            mx: 'auto',
                            mb: 5,
                            fontSize: { xs: '1rem', md: '1.125rem' },
                            lineHeight: 1.7
                        }}
                    >
                        Yapay zeka çözümlerimizin işletmenize katacağı değeri birlikte keşfedelim. Hemen bizimle iletişime geçin ve uzman ekibimizden demo talep edin.
                    </Typography>
                    <Button
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => window.open('https://dcat.com.tr/classic/tr/iletisim', '_blank')}
                        sx={{
                            px: 5,
                            py: 1.75,
                            fontSize: '1.05rem',
                            fontWeight: 700,
                            color: '#fff',
                            borderRadius: 999,
                            textTransform: 'none',
                            background: 'linear-gradient(90deg, #3b5cff 0%, #6a4cff 100%)',
                            boxShadow: '0 0 40px rgba(79, 92, 255, 0.55), 0 8px 24px rgba(0,0,0,0.35)',
                            transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                            '&:hover': {
                                background: 'linear-gradient(90deg, #4a6bff 0%, #7a5cff 100%)',
                                boxShadow: '0 0 55px rgba(106, 76, 255, 0.7), 0 10px 28px rgba(0,0,0,0.4)',
                                transform: 'translateY(-2px)'
                            }
                        }}
                    >
                        Hemen Başlayın
                    </Button>
                </Container>
            </Box>

        </Box>
    );
};

export default FranchiseManagement;
